import { acceptForumAnswerFn } from "@/actions/tags/handler";
import { ContextualContentCta } from "@/components/content/contextual-content-cta";
import { ForumFollowButton } from "@/components/forum/forum-follow-button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { forumCategoryLabels } from "@/utils/forum-catalog";
import { seo } from "@/utils/seo";
import { useMutation } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  redirect,
  useRouteContext,
} from "@tanstack/react-router";
import {
  CheckCircle2,
  ChevronRight,
  Clock,
  Eye,
  MessageSquare,
  MoreHorizontal,
  Share2,
  ShieldCheck,
  User,
} from "lucide-react";

import { getAllMessagesOptions } from "@/actions/messages/handlers";
import { getSelectedTagOptions } from "@/actions/tags/handler";
import { useSuspenseQuery } from "@tanstack/react-query";

import MessageArea from "@/components/messageArea";
import ReplyEditor from "@/components/replyEditor";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/forum/$tagId")({
  component: ForumThreadPage,
  loader: async ({ params, context }) => {
    const { tagId } = params;
    const { body: tagData } = await context.queryClient.ensureQueryData({
      ...getSelectedTagOptions({ tagId }),
      retry: 2,
    });

    if (!tagData.tag) {
      throw redirect({ to: "/forum" });
    }

    if (tagData.tag.moderationStatus === "merged" && tagData.tag.mergedIntoId) {
      const { body: merged } = await context.queryClient.ensureQueryData({
        ...getSelectedTagOptions({ tagId: tagData.tag.mergedIntoId }),
        retry: 2,
      });
      throw redirect({
        to: "/forum/$tagId",
        params: { tagId: merged.tag?.slug || tagData.tag.mergedIntoId },
        statusCode: 301,
      });
    }

    if (tagData.tag.slug && tagData.tag.slug !== tagId) {
      throw redirect({
        to: "/forum/$tagId",
        params: { tagId: tagData.tag.slug },
        statusCode: 301,
      });
    }

    const { body: messagesData } = await context.queryClient.ensureQueryData({
      ...getAllMessagesOptions({ tagId: tagData.tag.tagId }),
      retry: 2,
    });

    return {
      tag: tagData,
      messages: messagesData.messages.messages,
    };
  },
  head: ({ loaderData }) => {
    const tag = loaderData?.tag?.tag;
    const messages = loaderData?.messages;
    const expertAnswer = messages?.find((message) => message.isExpertAnswer);
    const acceptedAnswer = messages?.find(
      (message) => message.id === tag?.acceptedMessageId,
    );
    const isIndexable =
      tag?.moderationStatus === "published" && Boolean(expertAnswer);
    const title = tag?.tagTitle
      ? `${tag.tagTitle} | Forum | theCoach`
      : "Forum | theCoach";
    const description = tag?.tagContent
      ? tag.tagContent.slice(0, 160).trim() +
        (tag.tagContent.length > 160 ? "..." : "")
      : "theCoach forumunda konuları keşfedin, sorular sorun ve toplulukla etkileşime geçin.";

    return {
      meta: seo({
        title,
        description,
        keywords: `${tag?.tagTitle || ""}, ${tag?.category || ""}, powerlifting, fitness, forum`,
        canonical: `https://atalaythecoach.com/forum/${tag?.slug || tag?.tagId}`,
      }),
      ...(!isIndexable
        ? {
            meta: [
              ...seo({ title, description }),
              { name: "robots", content: "noindex, follow" },
            ],
          }
        : {}),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "QAPage",
            mainEntityOfPage: `https://atalaythecoach.com/forum/${tag?.slug || tag?.tagId}`,
            mainEntity: {
              "@type": "Question",
              name: tag?.tagTitle,
              text: tag?.tagContent,
              answerCount: messages?.length || 0,
              dateCreated: tag?.createdAt
                ? new Date(tag.createdAt).toISOString()
                : undefined,
              author: {
                "@type": "Person",
                name: tag?.creater?.userName || "Anonim",
              },
              acceptedAnswer:
                expertAnswer || acceptedAnswer
                  ? {
                      "@type": "Answer",
                      text: (expertAnswer || acceptedAnswer)?.messageData,
                      dateCreated: new Date(
                        (expertAnswer || acceptedAnswer)?.createdAt || 0,
                      ).toISOString(),
                      author: {
                        "@type": "Person",
                        name: (expertAnswer || acceptedAnswer)?.sender.userName,
                      },
                    }
                  : undefined,
              suggestedAnswer: messages
                ?.filter(
                  (message) =>
                    message.id !== expertAnswer?.id &&
                    message.id !== acceptedAnswer?.id,
                )
                .map((message) => ({
                  "@type": "Answer",
                  text: message.messageData,
                  dateCreated: new Date(message.createdAt).toISOString(),
                  author: {
                    "@type": "Person",
                    name: message.sender.userName,
                  },
                })),
            },
          }),
        },
      ],
    };
  },
  errorComponent: () => (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background pt-28 text-center text-foreground">
      <h1 className="font-bold font-serif text-4xl">Bir Hata Oluştu</h1>
      <p className="mt-4 text-muted-foreground">
        Konu yüklenirken bir sorun oluştu. Lütfen daha sonra tekrar deneyin.
      </p>
      <Button asChild className="mt-6">
        <Link to="/forum">Forum Anasayfasına Dön</Link>
      </Button>
    </div>
  ),
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col items-center justify-center pt-28 text-center">
      <h1 className="font-bold font-serif text-4xl">Konu Bulunamadı</h1>
      <p className="mt-4 text-muted-foreground">
        Aradığınız konu mevcut değil veya kaldırılmış olabilir.
      </p>
      <Button className="mt-6">
        <Link to="/forum">Tüm Konulara Geri Dön</Link>
      </Button>
    </div>
  ),
});

function ForumThreadPage() {
  const loaded = Route.useLoaderData();
  const tag = loaded.tag.tag;
  if (!tag) throw new Error("Soru bulunamadı");
  const { user } = useRouteContext({ from: "/_app" });
  const { data: messageResult } = useSuspenseQuery(
    getAllMessagesOptions({ tagId: tag.tagId }),
  );
  const messages = [...messageResult.body.messages.messages].sort((a, b) => {
    if (a.isExpertAnswer !== b.isExpertAnswer) return a.isExpertAnswer ? -1 : 1;
    if (a.id === tag.acceptedMessageId) return -1;
    if (b.id === tag.acceptedMessageId) return 1;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });
  const acceptAnswer = useMutation({
    mutationFn: (messageId: string | null) =>
      acceptForumAnswerFn({ data: { tagId: tag.tagId, messageId } }),
    onSuccess: () => {
      toast.success("Kabul edilen yanıt güncellendi.");
      window.location.reload();
    },
    onError: () => toast.error("Yanıt seçilemedi."),
  });
  const canAccept = user?.role === "admin" || user?.userId === tag.createdId;

  return (
    <div className="min-h-screen bg-background pt-28 pb-16 text-foreground sm:pt-32">
      <div className="container mx-auto max-w-5xl px-4 ">
        <nav className="mb-4 flex items-center space-x-2 text-muted-foreground text-sm">
          <Link to="/forum" className="hover:text-primary">
            Forum
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="font-semibold text-foreground">
            {forumCategoryLabels[tag.category]}
          </span>
        </nav>

        <article className="wrap-break-word">
          <h1 className="mb-2 font-bold font-serif text-3xl lg:text-4xl">
            {tag.tagTitle}
          </h1>
        </article>
        <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground text-sm">
          <div className="flex items-center">
            <User className="mr-1.5 h-4 w-4" /> {tag.creater.userName}
          </div>
          <div className="flex items-center">
            <Clock className="mr-1.5 h-4 w-4" />{" "}
            {tag.createdAt.toLocaleDateString()}
          </div>
          <div className="flex items-center">
            <MessageSquare className="mr-1.5 h-4 w-4" /> {messages?.length || 0}
          </div>
          <div className="flex items-center">
            <Eye className="mr-1.5 h-4 w-4" /> {tag.viewCount}
          </div>
          <ForumFollowButton
            questionId={tag.tagId}
            authenticated={Boolean(user)}
          />
        </div>
        <div className="mb-6 flex flex-wrap gap-2">
          <Badge variant="outline">{forumCategoryLabels[tag.category]}</Badge>
          {tag.topics.map((topic) => (
            <Badge key={topic} variant="secondary">
              {topic}
            </Badge>
          ))}
          {tag.moderationStatus === "pending_review" && (
            <Badge variant="outline">
              İnceleme bekliyor · yalnızca siz görebilirsiniz
            </Badge>
          )}
        </div>

        {/* Ana Gönderi Kartı */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="flex flex-col md:grid md:grid-cols-[200px_1fr]">
              <div className="flex flex-row items-center border-b bg-muted/30 p-4 text-center md:flex-col md:border-r md:border-b-0">
                <Avatar className="relative h-16 w-16 md:h-28 md:w-28">
                  <AvatarImage
                    src={tag.creater.image || ""}
                    alt={tag.creater.userName}
                  />
                  <AvatarFallback className="text-3xl">
                    {tag.creater.userName?.charAt(0).toUpperCase() || "U"}
                  </AvatarFallback>
                  {/* TODO: {post?.body.tag?.creater.online && (
                    <span className="absolute right-0 bottom-0 block h-4 w-4 rounded-full border-2 border-card bg-green-500 md:h-5 md:w-5" />
                  )} */}
                </Avatar>
                <div className="ml-4 grow text-left md:mt-3 md:ml-0 md:text-center">
                  <h3 className="font-bold text-lg text-primary">
                    {tag.creater.userName}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {/*TODO:  {post?.body.tag?.creater.rank} */}
                    Üye
                  </p>
                </div>
              </div>

              <div className="flex flex-col p-4 sm:p-6">
                <div className="mb-4 flex items-center justify-between text-muted-foreground text-xs">
                  <span>{tag.createdAt.toLocaleDateString()}</span>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        navigator.share({
                          title: tag.tagTitle,
                          url: window.location.href,
                        });
                      }}
                      title="Başlığı kopyala"
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                    <span className="font-semibold text-foreground">
                      #{tag.tagId}
                    </span>
                    {/* TODO:spam */}
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <article className="wrap-break-word space-y-4 font-sans text-base">
                  {tag.tagContent.split("\n").map((line, index) => (
                    <p className="max-w-2xl" key={`${tag.tagId}-${index}`}>
                      {line}
                    </p>
                  ))}
                </article>

                <Separator className="my-4" />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 space-y-6">
          {messages?.map((message) => (
            <Card
              key={message.id}
              id={`message-${message.id}`}
              className={
                tag.acceptedMessageId === message.id
                  ? "overflow-hidden border-emerald-500/60"
                  : "overflow-hidden"
              }
            >
              <CardContent className="p-0">
                <div className="flex flex-col md:grid md:grid-cols-[200px_1fr]">
                  <div className="flex flex-row items-center border-b bg-muted/30 p-4 text-center md:flex-col md:border-r md:border-b-0">
                    <Avatar className="relative h-16 w-16 md:h-28 md:w-28">
                      <AvatarImage
                        src={message.sender.image || ""}
                        alt={message.sender.userName}
                      />
                      <AvatarFallback className="text-3xl">
                        {message.sender.userName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="ml-4 grow text-left md:mt-3 md:ml-0 md:text-center">
                      <h3 className="font-bold text-lg text-primary">
                        {message.sender.userName}
                      </h3>
                      <div className="mt-1 flex flex-wrap justify-center gap-1.5">
                        {message.isExpertAnswer && (
                          <Badge className="bg-blue-600">
                            <ShieldCheck className="mr-1 h-3 w-3" />
                            Uzman Yanıtı
                          </Badge>
                        )}
                        {tag.acceptedMessageId === message.id && (
                          <Badge className="bg-emerald-600">
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            Kabul Edilen
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Cevap İçeriği (Sağ Taraf) */}
                  <div className="flex flex-col p-4 sm:p-6">
                    <div className="mb-4 flex items-center justify-between text-muted-foreground text-xs">
                      <span>
                        {new Date(message.createdAt).toLocaleDateString()}
                      </span>
                      <div className="flex items-center space-x-2">
                        {canAccept && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              acceptAnswer.mutate(
                                tag.acceptedMessageId === message.id
                                  ? null
                                  : message.id,
                              )
                            }
                          >
                            <CheckCircle2 className="mr-1 h-4 w-4" />
                            {tag.acceptedMessageId === message.id
                              ? "Seçimi kaldır"
                              : "Kabul et"}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <article className="space-y-4 break-all font-sans text-base">
                      {(() => {
                        try {
                          const parsed = JSON.parse(message.messageData);
                          if (
                            parsed &&
                            typeof parsed === "object" &&
                            (parsed.root || parsed.type === "doc")
                          ) {
                            return (
                              <MessageArea editorSerializedState={parsed} />
                            );
                          }
                        } catch (_e) {
                          // console.error("JSON parsing error:", e);
                        }
                        return message.messageData
                          .split("\n")
                          .map((line, i) => (
                            <p className="max-w-2xl break-all" key={i}>
                              {line}
                            </p>
                          ));
                      })()}
                    </article>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="mt-8">
          <ContextualContentCta
            href={tag.ctaHref}
            label={tag.ctaLabel}
            source="forum"
          />
        </div>
        <div className="mt-6" id="reply-form">
          <Card className="mt-6" id="reply-form">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start space-x-4">
                <Avatar>
                  <AvatarImage
                    src={user?.image || ""}
                    alt={user?.userName || undefined}
                  />
                  <AvatarFallback>
                    {user?.userName
                      ? user.userName.charAt(0).toUpperCase()
                      : "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex w-full max-w-10/12 flex-col md:max-w-3xl lg:max-w-4xl">
                  <ReplyEditor tagId={tag.tagId} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
